// Angular Files
import { SafeHtml } from '@angular/platform-browser';

// Teller Online Library Files
import { TerminologyDto } from 'libraries/core/src/lib/api/CoreWebApi';
import { escapeHtml } from 'teller-online-libraries/shared';

/**
 * HelpTag is now a string type to allow portal-specific tag enums.
 * Each portal should define its own enum for type safety (e.g., PublicHelpTag, ConfigHelpTag)
 */
export type HelpTag = string;

export enum HelpConditional {
    Users = 'Users',
    NotUsers = 'NotUsers',
    Redirect = 'Redirect',
    NotRedirect = 'NotRedirect',
    Fis = 'Fis',
    Tenant = 'Tenant',
    TOL = 'TellerOnlineLite',
    ScheduledPayments = 'ScheduledPayments',
    LinkedAccounts = 'LinkedAccounts'
}

/** The tenant's term for linked accounts in each |term:...| form, for titles and for bodies. */
export interface HelpTermForms {
    // Plain text, for titles, which render through interpolation
    text: Record<string, string>;
    // HTML-escaped, for bodies, which render as trusted HTML
    html: Record<string, string>;
}

/**
 * Builds the forms a |term:...| token can ask for, once per site:
 * |term:singular| and |term:plural| in sentence case, for the middle of a sentence;
 * |term:Singular| and |term:Plural| the same with the first letter capitalized, for the start;
 * |term:label-singular| and |term:label-plural| exactly as typed, inside a bold UI label.
 * Sentence case lowercases each word unless it has a capital past its first letter, so an
 * acronym such as "APN" survives.
 */
export function buildHelpTermForms(
    term: Pick<TerminologyDto, 'singular' | 'plural'>
): HelpTermForms {
    // The site metadata resolves both forms server-side; the fallback only keeps a malformed
    // response from printing "undefined" into an article
    const singular = term.singular ?? '';
    const plural = term.plural ?? '';
    const sentenceCase = (value: string) => value
        .split(' ')
        .map(word => (word.slice(1) === word.slice(1).toLowerCase() ? word.toLowerCase() : word))
        .join(' ');
    const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
    const text: Record<string, string> = {
        singular: sentenceCase(singular),
        plural: sentenceCase(plural),
        Singular: capitalize(sentenceCase(singular)),
        Plural: capitalize(sentenceCase(plural)),
        'label-singular': singular,
        'label-plural': plural
    };

    // The term is typed by an admin. Bodies skip Angular's sanitizer, so escape it (quotes
    // included, as bodies use single-quoted attributes), and encode colons so replaceIcons
    // cannot read the term as an :icon: placeholder
    const html: Record<string, string> = {};
    Object.entries(text).forEach(([form, value]) => {
        html[form] = escapeHtml(value).replace(/'/g, '&#39;').replace(/:/g, '&#58;');
    });

    return { text, html };
}

export interface HelpArticleProperties {
    tag: HelpTag;
    bookmark: string;
    title: string;
    body: string;
    order: number;
    group?: string;
    conditions?: HelpConditional[];
    tenants?: string[];
}

export interface HelpConditionContext {
    tenantName: string;
    processorName: string;
    isRedirect: boolean;
    authenticatedUsersEnabled: boolean;
    tellerOnlineLiteEnabled: boolean;
    scheduledPaymentsEnabled: boolean;
    linkedAccountsEnabled: boolean;
    linkedAccountTerm: Pick<TerminologyDto, 'singular' | 'plural'>;
}

export class HelpArticle {
    tag: HelpTag;

    bookmark: string;

    conditions?: HelpConditional[];

    title: string;

    body: string;

    trustedBody: SafeHtml;

    order: number;

    group?: string;

    tenants?: string[];

    constructor(properties: HelpArticleProperties) {
        this.tag = properties.tag;
        this.conditions = properties.conditions;
        this.tenants = properties.tenants;
        this.bookmark = properties.bookmark;
        this.title = properties.title;
        this.body = properties.body;
        this.order = properties.order;
        this.group = properties.group;
    }

    applyInlineConditions(context: HelpConditionContext): void {
        const regex = /\|condition:(?<condition>.*?)\|/;
        let match = regex.exec(this.body);
        while (match) {
            const condition = match.groups!.condition as HelpConditional;

            // If the condition doesn't match, we want to remove the whole block
            const remove = !this.checkCondition(condition, context);

            // remove the whole block of text
            if (remove) {
                const replace: RegExp = new RegExp(`\\|condition:${condition}\\|.*?\\|endcondition:${condition}\\|`, 'g');
                this.body = this.body.replace(replace, '');
            } else {
                // remove just the conditional, leave the block of text
                this.body = this.body.replace(`|condition:${condition}|`, '').replace(`|endcondition:${condition}|`, '');
            }

            match = regex.exec(this.body);
        }
    }

    /** Replaces |term:...| tokens with the tenant's term. See buildHelpTermForms. */
    applyTerms(forms: HelpTermForms): void {
        const token = /\|term:(singular|plural|Singular|Plural|label-singular|label-plural)\|/g;

        this.title = this.title.replace(token, (_match, form: string) => forms.text[form]);
        this.body = this.body.replace(token, (_match, form: string) => forms.html[form]);
    }

    checkCondition(condition: HelpConditional, context: HelpConditionContext): boolean {
        switch (condition) {
            case HelpConditional.NotRedirect:
                return !context.isRedirect;
            case HelpConditional.Redirect:
                return context.isRedirect;
            case HelpConditional.Fis:
                return context.processorName.toLowerCase() === 'fis';
            case HelpConditional.Tenant:
                return this.tenants?.some(t => t.toLowerCase() === context.tenantName.toLowerCase()) ?? false;
            case HelpConditional.Users:
                return context.authenticatedUsersEnabled;
            case HelpConditional.NotUsers:
                return !context.authenticatedUsersEnabled;
            case HelpConditional.TOL:
                return context.tellerOnlineLiteEnabled;
            case HelpConditional.ScheduledPayments:
                return context.scheduledPaymentsEnabled;
            case HelpConditional.LinkedAccounts:
                return context.linkedAccountsEnabled;
            default:
                return false;
        }
    }

    checkConditions(context: HelpConditionContext): boolean {
        return !this.conditions || this.conditions.every(condition => this.checkCondition(condition, context));
    }

    /** Replaces :icon-name: placeholders with inline Material font icon markup.
     * Article bodies are rendered via innerHTML, so the icon must be plain markup
     * (a font span) rather than an Angular component.
     */
    replaceIcons(): void {
        const regex = /[^https]:(?<icon>[^\s]*?):[^/]/;
        let match = regex.exec(this.body);
        while (match) {
            const { icon } = (match.groups!);
            this.body = this.body.replace(
                `:${icon}:`,
                `<span class="material-symbols-rounded" aria-hidden="true">${icon}</span>`);
            match = regex.exec(this.body);
        }
    }

    match(matchValue: string, tagMatch: boolean = false): boolean {
        return tagMatch
            ? this.tag === matchValue
            : this.body.includes(matchValue) || this.title.includes(matchValue) || this.tag.includes(matchValue);
    }
}
